import { useEffect, useState } from 'react';
import { Button, FlexContainer, InputMask, InputText, Message } from '@uigovpe/components';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { useToast } from '@/shared/providers/ToastProvider';
import { Profile } from '@/shared/types/certificate-request';
import { formatPhone } from '@/shared/utils/formatting';
import { ContactInput } from '@/features/certificate-request/api/profileApi';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

interface ContactVerificationFormProps {
  profile: Profile;
  busy: boolean;
  error?: string | null;
  codeRecipient?: string | null;
  onSaveContact: (input: ContactInput) => Promise<void>;
  onSendCode: () => Promise<boolean>;
  onVerifyCode: (code: string) => Promise<void>;
  /** Avisa quem envolve o formulário se o contato atual está confirmado (para habilitar/desabilitar ações externas, como "Revisar"). */
  onConfirmedChange?: (confirmed: boolean) => void;
  /**
   * Permite reabrir os campos para edição mesmo com o contato já confirmado.
   * Usado na tela de Perfil — no step de Contato do fluxo de solicitação
   * isso fica desligado, porque ali o contato recém-confirmado só segue para
   * a Revisão (trocar de novo é coisa da tela de Perfil).
   */
  allowReEdit?: boolean;
}

/**
 * E-mail/telefone + confirmação por código de 6 dígitos.
 *
 * Compartilhado entre a etapa 3 do fluxo de solicitação (primeira vez) e a
 * tela de Perfil (quando o cidadão decide trocar o contato depois). Trocar o
 * e-mail sempre exige novo código — a regra vive no backend
 * (`Requester.setEmail` derruba `emailConfirmedAt`), aqui só refletimos o
 * estado que volta da API.
 */
export function ContactVerificationForm({
  profile,
  busy,
  error,
  codeRecipient,
  onSaveContact,
  onSendCode,
  onVerifyCode,
  onConfirmedChange,
  allowReEdit = false,
}: ContactVerificationFormProps) {
  const { showToast } = useToast();
  const [email, setEmail] = useState(profile.email ?? '');
  const [emailConfirmation, setEmailConfirmation] = useState(profile.email ?? '');
  const [phone, setPhone] = useState(formatPhone(profile.phone));
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const [editing, setEditing] = useState(false);

  const phoneDigits = phone.replace(/\D/g, '');
  const errors = {
    email: EMAIL_PATTERN.test(email.trim()) ? '' : 'Informe um e-mail válido.',
    emailConfirmation:
      email.trim().toLowerCase() === emailConfirmation.trim().toLowerCase()
        ? ''
        : 'Os dois e-mails precisam ser iguais.',
    phone:
      phoneDigits.length > 0 && phoneDigits.length < 10 ? 'Informe o telefone com DDD.' : '',
  };
  const contactValid = !errors.email && !errors.emailConfirmation && !errors.phone;
  const errorOf = (field: keyof typeof errors) => (touched ? errors[field] : '');

  const emailChanged = email.trim().toLowerCase() !== (profile.email ?? '').toLowerCase();
  const alreadyConfirmed = profile.emailConfirmed && !emailChanged;
  const locked = alreadyConfirmed && !editing;

  useEffect(() => {
    onConfirmedChange?.(alreadyConfirmed);
  }, [alreadyConfirmed, onConfirmedChange]);

  // Depois de uma confirmação bem-sucedida, volta a fechar em resumo — quem
  // quiser trocar de novo clica em "Editar contato" outra vez.
  useEffect(() => {
    if (alreadyConfirmed) setEditing(false);
  }, [alreadyConfirmed]);

  if (locked) {
    return (
      <FlexContainer direction="col" gap="6">
        <ErrorNotice message={error} />
        <Message severity="success" text={`E-mail ${profile.email} confirmado.`} />
        {allowReEdit && (
          <div className="cd-actions">
            <Button
              label="Editar contato"
              outlined
              icon="pi pi-pencil"
              onClick={() => setEditing(true)}
            />
          </div>
        )}
      </FlexContainer>
    );
  }

  return (
    <FlexContainer direction="col" gap="6">
      <ErrorNotice message={error} />

      <div className="cd-grid cd-grid--2">
        <InputText
          inputId="contact-email"
          label="E-mail*"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={errorOf('email') ? 'p-invalid' : ''}
          suportText={errorOf('email')}
        />
        <InputText
          inputId="contact-email-confirmation"
          label="Confirme o e-mail*"
          value={emailConfirmation}
          onChange={(e) => setEmailConfirmation(e.target.value)}
          onPaste={(e) => e.preventDefault()}
          className={errorOf('emailConfirmation') ? 'p-invalid' : ''}
          suportText={errorOf('emailConfirmation') || 'Digite de novo, sem colar.'}
        />
      </div>

      <InputMask
        id="contact-phone"
        label="Telefone com DDD"
        mask="(99) 99999-9999"
        placeholder="(00) 00000-0000"
        value={phone}
        onChange={(e) => setPhone(e.value ?? '')}
        className={errorOf('phone') ? 'p-invalid' : ''}
        suportText={errorOf('phone')}
      />

      {codeRecipient ? (
        <div className="cd-code">
          <Message
            severity="info"
            text={`Enviamos um código de 6 dígitos para ${email.trim()}. Ele vale por 5 minutos.`}
          />
          <div className="cd-code__row">
            <InputMask
              id="contact-code"
              label="Código recebido"
              mask="999999"
              placeholder="000000"
              unmask
              value={code}
              onChange={(e) => setCode((e.value ?? '').replace(/\D/g, ''))}
            />
            <Button
              label="Confirmar"
              loading={busy}
              disabled={code.length !== 6}
              onClick={() => onVerifyCode(code)}
            />
          </div>
          <Button
            label="Enviar outro código"
            text
            size="small"
            loading={busy}
            onClick={async () => {
              const sent = await onSendCode();
              if (sent) {
                showToast({
                  severity: 'success',
                  summary: 'Código reenviado',
                  detail: `Enviamos um novo código para ${email.trim()}.`,
                });
              }
            }}
          />
        </div>
      ) : (
        <Button
          label="Salvar e enviar código por e-mail"
          icon="pi pi-envelope"
          loading={busy}
          onClick={async () => {
            setTouched(true);
            if (!contactValid) return;
            await onSaveContact({
              email: email.trim(),
              phone: phoneDigits || undefined,
            });
            await onSendCode();
          }}
        />
      )}
    </FlexContainer>
  );
}
